/**
 * ContentFilteringMockup — Interactive mockups for the content filtering feature.
 * Shows: Extension sidebar (Filters + Elements tabs), Web UI admin, App list indicators.
 */

import React, { useState } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Chip, Switch, IconButton, TextField,
  List, ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction,
  Button, Divider, Alert, Tooltip, FormControl, InputLabel, Select, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, Badge, InputAdornment,
  Autocomplete, LinearProgress,
} from '@mui/material';
import {
  Shield as ShieldIcon, Block as BlockIcon, CheckCircle as AllowIcon,
  Delete as DeleteIcon, Add as AddIcon, Refresh as RefreshIcon,
  Visibility as VisibilityIcon, VisibilityOff as HideIcon,
  TouchApp as PickerIcon, AutoFixHigh as AiIcon, Search as SearchIcon,
  Warning as WarningIcon, Language as DomainIcon, Code as RegexIcon,
  Link as UrlIcon, ArrowBack as BackIcon, Close as CloseIcon,
  NetworkCheck as NetworkIcon, Layers as ElementsIcon,
  PlayArrow as PlayIcon, Edit as EditIcon,
} from '@mui/icons-material';

// ─── Mock Data ───────────────────────────────────────────────

const MOCK_NETWORK_RULES = [
  // Global blocks — domains
  { id: '1', name: 'Block Facebook', pattern: 'facebook.com', ruleType: 'block', target: 'domain', isActive: true, priority: 5, appDefinitionId: null, category: 'social_media', hits: 47 },
  { id: '2', name: 'Block Twitter/X', pattern: 'twitter.com', ruleType: 'block', target: 'domain', isActive: true, priority: 5, appDefinitionId: null, category: 'social_media', hits: 12 },
  { id: '3', name: 'Block Instagram', pattern: 'instagram.com', ruleType: 'block', target: 'domain', isActive: true, priority: 5, appDefinitionId: null, category: 'social_media', hits: 3 },
  { id: '4', name: 'Block Discord', pattern: 'discord.com', ruleType: 'block', target: 'domain', isActive: true, priority: 5, appDefinitionId: null, category: 'messaging', hits: 0 },
  // Global blocks — URL patterns (Squid/PAC style)
  { id: '5', name: 'Block Facebook tracking pixel', pattern: '*://connect.facebook.net/*/fbevents.js', ruleType: 'block', target: 'url_pattern', isActive: true, priority: 8, appDefinitionId: null, category: 'social_media', hits: 23 },
  { id: '6', name: 'Block social share endpoints', pattern: '*://*/share?url=*', ruleType: 'block', target: 'url_pattern', isActive: true, priority: 6, appDefinitionId: null, category: 'social_media', hits: 8 },
  { id: '7', name: 'Block mailto links', pattern: '*://*/mailto:*', ruleType: 'block', target: 'url_pattern', isActive: true, priority: 5, appDefinitionId: null, category: 'email', hits: 1 },
  // Global blocks — regex (PAC script style)
  { id: '8', name: 'Block all chat widgets', pattern: '^https?://(.*\\.)?(intercom|drift|crisp|tawk|zendesk)\\..*', ruleType: 'block', target: 'regex', isActive: true, priority: 7, appDefinitionId: null, category: 'messaging', hits: 15 },
  { id: '9', name: 'Block gambling domains', pattern: '^https?://(.*\\.)?(bet365|draftkings|fanduel|pokerstars)\\..*', ruleType: 'block', target: 'regex', isActive: true, priority: 7, appDefinitionId: null, category: 'gambling', hits: 0 },
  { id: '10', name: 'Block VPN/proxy services', pattern: '^https?://(.*\\.)?(nordvpn|expressvpn|surfshark|protonvpn|hidemyass)\\..*', ruleType: 'block', target: 'regex', isActive: true, priority: 7, appDefinitionId: null, category: 'vpn_proxy', hits: 0 },
  // Per-app allows — domains
  { id: '11', name: 'Allow Khan Academy', pattern: 'khanacademy.org', ruleType: 'allow', target: 'domain', isActive: true, priority: 10, appDefinitionId: 'khan', category: 'education', hits: 184 },
  { id: '12', name: 'Allow Khan CDN', pattern: 'kastatic.org', ruleType: 'allow', target: 'domain', isActive: true, priority: 10, appDefinitionId: 'khan', category: 'education', hits: 312 },
  { id: '13', name: 'Allow Khan Sandbox', pattern: 'kasandbox.org', ruleType: 'allow', target: 'domain', isActive: true, priority: 10, appDefinitionId: 'khan', category: 'education', hits: 89 },
  // Per-app allows — URL patterns (granular path allowlisting)
  { id: '14', name: 'Allow Khan exercise API', pattern: '*://khanacademy.org/api/internal/*', ruleType: 'allow', target: 'url_pattern', isActive: true, priority: 12, appDefinitionId: 'khan', category: 'education', hits: 56 },
  { id: '15', name: 'Allow Khan Perseus images', pattern: '*://ka-perseus-images.s3.amazonaws.com/*', ruleType: 'allow', target: 'url_pattern', isActive: true, priority: 10, appDefinitionId: 'khan', category: 'education', hits: 41 },
  // Per-app allows — regex (complex CDN patterns)
  { id: '16', name: 'Allow Khan video CDN', pattern: '^https://[a-z0-9]+\\.googlevideo\\.com/videoplayback\\?.*itag=(18|22|37).*', ruleType: 'allow', target: 'regex', isActive: true, priority: 11, appDefinitionId: 'khan', category: 'education', hits: 7 },
  // Per-app blocks (app-specific restrictions)
  { id: '17', name: 'Block Khan community/social', pattern: '*://khanacademy.org/profile/*/discussion*', ruleType: 'block', target: 'url_pattern', isActive: true, priority: 15, appDefinitionId: 'khan', category: 'social_media', hits: 4 },
  { id: '18', name: 'Block Khan external links', pattern: '^https://khanacademy\\.org/computer-programming/.*/redirect\\?url=.*', ruleType: 'block', target: 'regex', isActive: true, priority: 14, appDefinitionId: 'khan', category: 'social_media', hits: 2 },
];

const MOCK_ELEMENT_RULES = [
  { id: '1', selector: 'button[data-testid="share-facebook"]', description: 'Facebook share button', isActive: true, suggestedBlock: 'facebook.com' },
  { id: '2', selector: '.social-share-widget', description: 'Social sharing widget', isActive: true, suggestedBlock: null },
  { id: '3', selector: '#chat-widget-container', description: 'Live chat widget (Intercom)', isActive: true, suggestedBlock: 'intercom.io' },
  { id: '4', selector: 'a[href*="mailto:"]', description: 'Email links', isActive: true, suggestedBlock: null },
  { id: '5', selector: 'iframe[src*="youtube.com"]', description: 'YouTube embedded videos', isActive: false, suggestedBlock: 'youtube.com' },
];

const MOCK_LIVE_REQUESTS = [
  { url: 'https://www.khanacademy.org/math/algebra', status: 200, type: 'document', size: '142 KB', time: '423ms', blocked: false, matchedRules: [] },
  { url: 'https://kastatic.org/genfiles/ka-exercise-bundle.js', status: 200, type: 'script', size: '892 KB', time: '187ms', blocked: false, matchedRules: [
    { name: 'Allow Khan CDN', pattern: 'kastatic.org', target: 'domain', ruleType: 'allow' },
  ]},
  { url: 'https://khanacademy.org/api/internal/user/profile', status: 200, type: 'xhr', size: '4.2 KB', time: '95ms', blocked: false, matchedRules: [
    { name: 'Allow Khan exercise API', pattern: '*://khanacademy.org/api/internal/*', target: 'url_pattern', ruleType: 'allow' },
  ]},
  { url: 'https://ka-perseus-images.s3.amazonaws.com/math/triangle.svg', status: 200, type: 'image', size: '3.1 KB', time: '45ms', blocked: false, matchedRules: [
    { name: 'Allow Khan Perseus images', pattern: '*://ka-perseus-images.s3.amazonaws.com/*', target: 'url_pattern', ruleType: 'allow' },
  ]},
  { url: 'https://kasandbox.org/genfiles/ka-exercise.js', status: 200, type: 'script', size: '128 KB', time: '112ms', blocked: false, matchedRules: [
    { name: 'Allow Khan Sandbox', pattern: 'kasandbox.org', target: 'domain', ruleType: 'allow' },
  ]},
  { url: 'https://connect.facebook.net/en_US/fbevents.js', status: 0, type: 'script', size: '-', time: '-', blocked: true, matchedRules: [
    { name: 'Block Facebook tracking pixel', pattern: '*://connect.facebook.net/*/fbevents.js', target: 'url_pattern', ruleType: 'block' },
    { name: 'Block Facebook', pattern: 'facebook.com', target: 'domain', ruleType: 'block' },
  ]},
  { url: 'https://www.facebook.com/tr?id=123&ev=PageView', status: 0, type: 'xhr', size: '-', time: '-', blocked: true, matchedRules: [
    { name: 'Block Facebook', pattern: 'facebook.com', target: 'domain', ruleType: 'block' },
  ]},
  { url: 'https://platform.twitter.com/widgets.js', status: 0, type: 'script', size: '-', time: '-', blocked: true, matchedRules: [
    { name: 'Block Twitter/X', pattern: 'twitter.com', target: 'domain', ruleType: 'block' },
    { name: 'Block social share endpoints', pattern: '*://*/share?url=*', target: 'url_pattern', ruleType: 'block' },
  ]},
  { url: 'https://widget.intercom.io/widget/abc123', status: 0, type: 'script', size: '-', time: '-', blocked: true, matchedRules: [
    { name: 'Block all chat widgets', pattern: '^https?://(.*\\.)?(intercom|drift|crisp|tawk|zendesk)\\..*', target: 'regex', ruleType: 'block' },
  ]},
  { url: 'https://www.google-analytics.com/analytics.js', status: 200, type: 'script', size: '44 KB', time: '67ms', blocked: false, matchedRules: [] },
  { url: 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js', status: 200, type: 'script', size: '298 KB', time: '234ms', blocked: false, matchedRules: [] },
  { url: 'https://khanacademy.org/profile/testuser/discussion/recent', status: 0, type: 'xhr', size: '-', time: '-', blocked: true, matchedRules: [
    { name: 'Block Khan community/social', pattern: '*://khanacademy.org/profile/*/discussion*', target: 'url_pattern', ruleType: 'block' },
  ]},
  { url: 'https://fonts.googleapis.com/css2?family=Lato', status: 200, type: 'stylesheet', size: '1.2 KB', time: '28ms', blocked: false, matchedRules: [] },
  { url: 'https://r3---sn-abc.googlevideo.com/videoplayback?itag=22&source=youtube', status: 200, type: 'media', size: '14.2 MB', time: '1.8s', blocked: false, matchedRules: [
    { name: 'Allow Khan video CDN', pattern: '^https://[a-z0-9]+\\.googlevideo\\.com/videoplayback\\?.*itag=(18|22|37).*', target: 'regex', ruleType: 'allow' },
  ]},
  { url: 'https://snap.licdn.com/li.lms-analytics/insight.min.js', status: 0, type: 'script', size: '-', time: '-', blocked: false, matchedRules: [] },
  { url: 'https://js.stripe.com/v3/', status: 200, type: 'script', size: '56 KB', time: '89ms', blocked: false, matchedRules: [] },
];

const MOCK_AI_SUGGESTIONS = [
  { type: 'block', pattern: 'snap.licdn.com', target: 'domain', reason: 'LinkedIn analytics tracker detected — not needed for education', category: 'social_media', confidence: 0.95 },
  { type: 'block', pattern: '*://*/share?url=*', target: 'url_pattern', reason: 'Social share endpoint pattern found in page links', category: 'social_media', confidence: 0.92 },
  { type: 'block', pattern: '^https?://(.*\\.)?stripe\\.com/.*', target: 'regex', reason: 'Payment processor — no purchases allowed in correctional environment', category: 'file_sharing', confidence: 0.88 },
  { type: 'block', pattern: '*://www.google-analytics.com/*', target: 'url_pattern', reason: 'Analytics tracking not needed, reduces data leakage', category: 'vpn_proxy', confidence: 0.72 },
  { type: 'hide', selector: '.share-buttons-container', reason: 'Social sharing buttons detected', confidence: 0.90 },
  { type: 'hide', selector: 'a[href*="reddit.com"]', reason: 'External link to social platform', confidence: 0.85 },
  { type: 'hide', selector: 'div[class*="premium"], div[class*="upgrade"]', reason: 'Premium/upgrade prompts — not applicable in managed environment', confidence: 0.80 },
];

const MOCK_APPS_WITH_FILTERS = [
  { appKey: 'khan', name: 'Khan Academy', scripts: 6, networkRules: 3, elementRules: 5, status: 'configured' },
  { appKey: 'coursera', name: 'Coursera', scripts: 2, networkRules: 2, elementRules: 0, status: 'partial' },
  { appKey: 'readtheory', name: 'ReadTheory', scripts: 3, networkRules: 0, elementRules: 0, status: 'none' },
  { appKey: 'careersafe', name: 'CareerSafe', scripts: 3, networkRules: 2, elementRules: 3, status: 'configured' },
  { appKey: 'icivics', name: 'iCivics', scripts: 3, networkRules: 0, elementRules: 0, status: 'none' },
  { appKey: 'duolingo', name: 'Duolingo', scripts: 1, networkRules: 1, elementRules: 2, status: 'partial' },
];

const CATEGORY_COLORS = {
  social_media: '#e91e63', messaging: '#9c27b0', email: '#673ab7',
  video_chat: '#3f51b5', education: '#4caf50', adult_content: '#f44336',
  gambling: '#ff5722', gaming: '#ff9800', vpn_proxy: '#795548',
  file_sharing: '#607d8b',
};

const CATEGORY_LABELS = {
  social_media: 'Social Media', messaging: 'Messaging', email: 'Email',
  video_chat: 'Video Chat', education: 'Education', adult_content: 'Adult',
  gambling: 'Gambling', gaming: 'Gaming', vpn_proxy: 'VPN/Proxy',
  file_sharing: 'File Sharing',
};

// ─── Helper Components ───────────────────────────────────────

const RuleTypeChip = ({ type }) => (
  <Chip
    icon={type === 'block' ? <BlockIcon sx={{ fontSize: 14 }} /> : <AllowIcon sx={{ fontSize: 14 }} />}
    label={type === 'block' ? 'Block' : 'Allow'}
    size="small"
    sx={{
      bgcolor: type === 'block' ? '#ffebee' : '#e8f5e9',
      color: type === 'block' ? '#c62828' : '#2e7d32',
      fontWeight: 600, fontSize: '0.7rem', height: 22,
    }}
  />
);

const TargetIcon = ({ target, showLabel }) => {
  const config = {
    regex: { icon: <RegexIcon sx={{ fontSize: 16 }} />, color: '#9c27b0', label: 'regex' },
    url_pattern: { icon: <UrlIcon sx={{ fontSize: 16 }} />, color: '#1976d2', label: 'URL' },
    domain: { icon: <DomainIcon sx={{ fontSize: 16 }} />, color: '#455a64', label: 'domain' },
  };
  const c = config[target] || config.domain;
  if (showLabel) {
    return (
      <Chip icon={c.icon} label={c.label} size="small"
        sx={{ height: 18, fontSize: '0.6rem', color: c.color, borderColor: c.color, '& .MuiChip-icon': { color: c.color } }}
        variant="outlined" />
    );
  }
  return <Tooltip title={c.label}>{React.cloneElement(c.icon, { sx: { fontSize: 16, color: c.color } })}</Tooltip>;
};

const CategoryChip = ({ category }) => (
  <Chip
    label={CATEGORY_LABELS[category] || category}
    size="small"
    sx={{
      bgcolor: (CATEGORY_COLORS[category] || '#999') + '20',
      color: CATEGORY_COLORS[category] || '#999',
      fontWeight: 600, fontSize: '0.65rem', height: 20,
    }}
  />
);

const FilterStatusBadge = ({ status, networkRules, elementRules }) => {
  if (status === 'none') {
    return (
      <Tooltip title="No content filters configured">
        <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label="No filters" size="small"
          sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.7rem' }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={`${networkRules} network rules, ${elementRules} element rules`}>
      <Chip
        icon={<ShieldIcon sx={{ fontSize: 14 }} />}
        label={`${networkRules + elementRules} rules`}
        size="small"
        sx={{
          bgcolor: status === 'configured' ? '#e8f5e9' : '#fff8e1',
          color: status === 'configured' ? '#2e7d32' : '#f57f17',
          fontWeight: 600, fontSize: '0.7rem',
        }}
      />
    </Tooltip>
  );
};

// ─── Helper: generate suggested patterns from a URL ─────────

const suggestPatternsFromUrl = (url) => {
  try {
    const u = new URL(url);
    const domain = u.hostname.replace(/^www\./, '');
    const pathParts = u.pathname.split('/').filter(Boolean);
    const suggestions = [
      { target: 'domain', pattern: domain, label: `Block entire domain: ${domain}` },
    ];
    if (u.pathname !== '/' && pathParts.length > 0) {
      suggestions.push({
        target: 'url_pattern',
        pattern: `*://${u.hostname}/${pathParts[0]}/*`,
        label: `Block path: ${u.hostname}/${pathParts[0]}/*`,
      });
    }
    if (pathParts.length > 1) {
      const pathRegex = `^https?://${domain.replace(/\./g, '\\.')}/${pathParts[0]}/.*`;
      suggestions.push({
        target: 'regex',
        pattern: pathRegex,
        label: `Block regex: ${domain}/${pathParts[0]}/.*`,
      });
    }
    // Wildcard subdomain pattern
    const baseDomain = domain.split('.').slice(-2).join('.');
    if (baseDomain !== domain) {
      suggestions.push({
        target: 'regex',
        pattern: `^https?://(.*\\.)?${baseDomain.replace(/\./g, '\\.')}(/.*)?$`,
        label: `Block all subdomains: *.${baseDomain}`,
      });
    }
    return suggestions;
  } catch { return []; }
};

// ─── DevTools-style Network Panel ────────────────────────────

const TYPE_FILTER_CHIPS = ['All', 'document', 'script', 'xhr', 'stylesheet', 'image', 'media'];

const NetworkDevToolsPanel = ({ onAddBlockRule }) => {
  const [typeFilter, setTypeFilter] = useState('All');
  const [showBlocked, setShowBlocked] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedReq, setSelectedReq] = useState(null);

  const filtered = MOCK_LIVE_REQUESTS.filter(r => {
    if (typeFilter !== 'All' && r.type !== typeFilter) return false;
    if (showBlocked === 'blocked' && !r.blocked) return false;
    if (showBlocked === 'allowed' && r.blocked) return false;
    if (search && !r.url.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const blockedCount = MOCK_LIVE_REQUESTS.filter(r => r.blocked).length;
  const totalCount = MOCK_LIVE_REQUESTS.length;
  const sel = selectedReq !== null ? filtered[selectedReq] : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Summary bar */}
      <Box sx={{ px: 1.5, py: 0.5, bgcolor: '#263238', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: '#b0bec5', fontFamily: 'monospace' }}>
          {totalCount} requests
        </Typography>
        <Typography variant="caption" sx={{ color: '#ef5350', fontFamily: 'monospace' }}>
          {blockedCount} blocked
        </Typography>
        <Typography variant="caption" sx={{ color: '#66bb6a', fontFamily: 'monospace' }}>
          {totalCount - blockedCount} allowed
        </Typography>
        <Box sx={{ flex: 1 }} />
        <LinearProgress variant="determinate" value={(blockedCount / totalCount) * 100}
          sx={{ width: 60, height: 4, borderRadius: 2, bgcolor: '#4caf50',
            '& .MuiLinearProgress-bar': { bgcolor: '#f44336' } }} />
      </Box>

      {/* Filter toolbar */}
      <Box sx={{ px: 1, py: 0.5, display: 'flex', gap: 0.5, borderBottom: '1px solid #37474f', bgcolor: '#37474f', flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Filter URLs..." value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 100,
            '& .MuiInputBase-input': { fontSize: '0.7rem', color: '#eceff1', py: 0.3, fontFamily: 'monospace' },
            '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#546e7a' } },
          }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: '#90a4ae' }} /></InputAdornment> }}
        />
      </Box>
      <Box sx={{ px: 1, py: 0.3, display: 'flex', gap: 0.3, bgcolor: '#37474f', borderBottom: '1px solid #546e7a', flexWrap: 'wrap' }}>
        {TYPE_FILTER_CHIPS.map(t => (
          <Chip key={t} label={t} size="small" onClick={() => setTypeFilter(t)}
            sx={{ height: 18, fontSize: '0.6rem', fontFamily: 'monospace',
              bgcolor: typeFilter === t ? '#1976d2' : 'transparent',
              color: typeFilter === t ? '#fff' : '#90a4ae',
              border: typeFilter === t ? 'none' : '1px solid #546e7a',
              '&:hover': { bgcolor: typeFilter === t ? '#1976d2' : '#455a64' } }} />
        ))}
        <Box sx={{ flex: 1 }} />
        {['all', 'blocked', 'allowed'].map(f => (
          <Chip key={f} label={f} size="small" onClick={() => setShowBlocked(f)}
            sx={{ height: 18, fontSize: '0.6rem', textTransform: 'capitalize',
              bgcolor: showBlocked === f ? (f === 'blocked' ? '#c62828' : f === 'allowed' ? '#2e7d32' : '#1976d2') : 'transparent',
              color: showBlocked === f ? '#fff' : '#90a4ae',
              border: showBlocked === f ? 'none' : '1px solid #546e7a' }} />
        ))}
      </Box>

      {/* Request table */}
      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#1a1a2e' }}>
        <Box sx={{ display: 'flex', px: 1, py: 0.3, borderBottom: '1px solid #37474f', position: 'sticky', top: 0, bgcolor: '#263238', zIndex: 1 }}>
          <Typography variant="caption" sx={{ width: 30, color: '#78909c', fontSize: '0.6rem' }} />
          <Typography variant="caption" sx={{ flex: 1, color: '#78909c', fontSize: '0.6rem', fontWeight: 600 }}>URL</Typography>
          <Typography variant="caption" sx={{ width: 50, color: '#78909c', fontSize: '0.6rem', fontWeight: 600, textAlign: 'center' }}>Status</Typography>
          <Typography variant="caption" sx={{ width: 55, color: '#78909c', fontSize: '0.6rem', fontWeight: 600, textAlign: 'center' }}>Type</Typography>
          <Typography variant="caption" sx={{ width: 50, color: '#78909c', fontSize: '0.6rem', fontWeight: 600, textAlign: 'right' }}>Size</Typography>
          <Typography variant="caption" sx={{ width: 45, color: '#78909c', fontSize: '0.6rem', fontWeight: 600, textAlign: 'right' }}>Time</Typography>
        </Box>
        {filtered.map((req, i) => (
          <Box key={i} onClick={() => setSelectedReq(selectedReq === i ? null : i)}
            sx={{ display: 'flex', px: 1, py: 0.2, cursor: 'pointer', borderBottom: '1px solid #1e1e3a',
              bgcolor: req.blocked ? '#2d0a0a' : (selectedReq === i ? '#1a237e' : 'transparent'),
              '&:hover': { bgcolor: req.blocked ? '#3d1a1a' : '#1a237e44' } }}>
            <Box sx={{ width: 30, display: 'flex', alignItems: 'center' }}>
              {req.blocked
                ? <BlockIcon sx={{ fontSize: 12, color: '#ef5350' }} />
                : <AllowIcon sx={{ fontSize: 12, color: '#66bb6a' }} />}
            </Box>
            <Typography variant="caption" noWrap sx={{
              flex: 1, fontFamily: 'monospace', fontSize: '0.68rem',
              color: req.blocked ? '#ef9a9a' : '#e0e0e0',
              textDecoration: req.blocked ? 'line-through' : 'none' }}>
              {req.url.replace(/^https?:\/\//, '')}
            </Typography>
            <Typography variant="caption" sx={{ width: 50, textAlign: 'center', fontFamily: 'monospace', fontSize: '0.65rem',
              color: req.blocked ? '#ef5350' : (req.status >= 400 ? '#ff9800' : '#66bb6a') }}>
              {req.blocked ? 'BLOCKED' : req.status}
            </Typography>
            <Typography variant="caption" sx={{ width: 55, textAlign: 'center', fontSize: '0.6rem', color: '#90a4ae' }}>{req.type}</Typography>
            <Typography variant="caption" sx={{ width: 50, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.65rem', color: '#b0bec5' }}>{req.size}</Typography>
            <Typography variant="caption" sx={{ width: 45, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.65rem', color: '#b0bec5' }}>{req.time}</Typography>
          </Box>
        ))}

        {/* Detail panel for selected request */}
        {sel && (
          <Box sx={{ p: 1.5, bgcolor: '#263238', borderTop: '2px solid #1976d2', m: 0.5, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: '#90caf9', fontWeight: 600, display: 'block', mb: 0.5 }}>
              Request Details
            </Typography>
            <Typography variant="caption" sx={{ color: '#e0e0e0', fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all', display: 'block' }}>
              {sel.url}
            </Typography>

            {/* Matched rules list */}
            {sel.matchedRules?.length > 0 && (
              <Box sx={{ mt: 0.8, p: 0.8, bgcolor: sel.blocked ? '#3d1a1a' : '#1a2e1a', borderRadius: 1, border: `1px solid ${sel.blocked ? '#5a2020' : '#2e5a20'}` }}>
                <Typography variant="caption" sx={{ color: sel.blocked ? '#ef9a9a' : '#a5d6a7', fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem' }}>
                  {sel.blocked ? `Blocked by ${sel.matchedRules.length} rule${sel.matchedRules.length > 1 ? 's' : ''}:` : `Allowed by:`}
                </Typography>
                {sel.matchedRules.map((rule, ri) => (
                  <Box key={ri} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.2, pl: 0.5,
                    borderLeft: `2px solid ${rule.ruleType === 'block' ? '#ef5350' : '#66bb6a'}` }}>
                    <TargetIcon target={rule.target} showLabel />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: '#e0e0e0', fontSize: '0.68rem', display: 'block', fontWeight: 600 }}>
                        {rule.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#90a4ae', fontFamily: 'monospace', fontSize: '0.65rem', display: 'block', wordBreak: 'break-all' }}>
                        {rule.pattern}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {!sel.blocked && (
              <Box sx={{ mt: 1, p: 1, bgcolor: '#1a1a2e', borderRadius: 1, border: '1px solid #37474f' }}>
                <Typography variant="caption" sx={{ color: '#ef9a9a', fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Block this request?
                </Typography>
                <Typography variant="caption" sx={{ color: '#90a4ae', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                  Choose a pattern — click to add as a new rule:
                </Typography>
                {suggestPatternsFromUrl(sel.url).map((s, i) => (
                  <Box key={i}
                    onClick={(e) => { e.stopPropagation(); onAddBlockRule && onAddBlockRule(s); }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5, py: 0.3, px: 0.5,
                      cursor: 'pointer', borderRadius: 0.5, mb: 0.3,
                      '&:hover': { bgcolor: '#37474f' },
                    }}>
                    <TargetIcon target={s.target} showLabel />
                    <Typography variant="caption" sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.68rem', color: '#e0e0e0' }}>
                      {s.pattern}
                    </Typography>
                    <AddIcon sx={{ fontSize: 14, color: '#90caf9' }} />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Mockup 1: Extension Sidebar — Filters Tab ──────────────

const ExtensionFiltersTab = () => {
  const [rules, setRules] = useState(MOCK_NETWORK_RULES);
  const [showLive, setShowLive] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [prefillRule, setPrefillRule] = useState(null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Blocker status bar */}
      <Box sx={{ px: 1.5, py: 1, bgcolor: '#e8f5e9', display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #c8e6c9' }}>
        <ShieldIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
        <Typography variant="caption" fontWeight={600} color="#2e7d32" sx={{ flex: 1 }}>
          {rules.filter(r => r.isActive).length} rules — {rules.reduce((s, r) => s + (r.hits || 0), 0)} hits this session
        </Typography>
        <Tooltip title="Reset hit counts">
          <IconButton size="small" onClick={() => setRules(rules.map(r => ({ ...r, hits: 0 })))}>
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666' }}>Reset</Typography>
          </IconButton>
        </Tooltip>
        <Tooltip title="Reload filters">
          <IconButton size="small"><RefreshIcon sx={{ fontSize: 16 }} /></IconButton>
        </Tooltip>
      </Box>

      {/* View toggle */}
      <Box sx={{ px: 1.5, py: 0.5, display: 'flex', gap: 1, borderBottom: '1px solid #eee' }}>
        <Chip label="Rules" size="small" variant={!showLive ? 'filled' : 'outlined'}
          color={!showLive ? 'primary' : 'default'} onClick={() => setShowLive(false)} />
        <Chip label="Live Requests" size="small" variant={showLive ? 'filled' : 'outlined'}
          color={showLive ? 'primary' : 'default'} onClick={() => setShowLive(true)} />
      </Box>

      {/* Rules list */}
      {!showLive ? (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* Global rules */}
          {/* Render rules grouped by section */}
          {[
            { label: 'Global Rules', filter: r => !r.appDefinitionId },
            { label: 'Khan Academy Rules', filter: r => r.appDefinitionId === 'khan' },
          ].map(section => {
            const sectionRules = rules.filter(section.filter);
            return (
              <Box key={section.label}>
                <Typography variant="caption" fontWeight={700} sx={{ px: 1.5, py: 0.5, display: 'block', bgcolor: '#f5f5f5' }}>
                  {section.label} ({sectionRules.length})
                </Typography>
                <List dense disablePadding>
                  {sectionRules.map(rule => (
                    <ListItem key={rule.id} sx={{ py: 0.4, px: 1.5, borderBottom: '1px solid #f5f5f5', alignItems: 'flex-start' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <RuleTypeChip type={rule.ruleType} />
                            <TargetIcon target={rule.target} showLabel />
                            {rule.category && <CategoryChip category={rule.category} />}
                            {rule.hits > 0 && (
                              <Chip label={`${rule.hits} hits`} size="small"
                                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, ml: 'auto',
                                  bgcolor: rule.ruleType === 'block' ? '#ffcdd2' : '#c8e6c9',
                                  color: rule.ruleType === 'block' ? '#b71c1c' : '#1b5e20' }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" sx={{
                            fontFamily: 'monospace', fontSize: '0.75rem', mt: 0.3, display: 'block',
                            color: rule.ruleType === 'block' ? '#c62828' : '#2e7d32',
                            wordBreak: 'break-all',
                          }}>
                            {rule.pattern}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Switch size="small" checked={rule.isActive} sx={{ mr: -1 }} />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>
            );
          })}

          {/* Add rule inline form */}
          {showAdd ? (
            <Box sx={{ p: 1.5, bgcolor: prefillRule ? '#fff8e1' : '#fafafa', borderTop: prefillRule ? '2px solid #ff9800' : '1px solid #e0e0e0' }}>
              {prefillRule && (
                <Alert severity="info" sx={{ mb: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                  Suggested from live request — edit pattern below if needed
                </Alert>
              )}
              <TextField
                fullWidth size="small" label="Pattern"
                defaultValue={prefillRule?.pattern || ''}
                placeholder="domain.com, *://host/path/*, or ^regex$"
                sx={{ mb: 1, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Type</InputLabel>
                  <Select defaultValue="block" label="Type">
                    <MenuItem value="block">Block</MenuItem>
                    <MenuItem value="allow">Allow</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Target</InputLabel>
                  <Select defaultValue={prefillRule?.target || 'domain'} label="Target">
                    <MenuItem value="domain">Domain</MenuItem>
                    <MenuItem value="url_pattern">URL Pattern</MenuItem>
                    <MenuItem value="regex">Regex</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {prefillRule && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.7rem' }}>
                  Suggested: <strong>{prefillRule.label}</strong>
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" fullWidth
                  onClick={() => { setShowAdd(false); setPrefillRule(null); }}>
                  Add Rule
                </Button>
                <Button variant="outlined" size="small"
                  onClick={() => { setShowAdd(false); setPrefillRule(null); }}>
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 1.5 }}>
              <Button startIcon={<AddIcon />} size="small" variant="outlined" fullWidth onClick={() => setShowAdd(true)}>
                Add Rule
              </Button>
            </Box>
          )}

          {/* AI Suggestions */}
          <Box sx={{ p: 1.5, bgcolor: '#f3e5f5', borderTop: '2px solid #ce93d8' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <AiIcon sx={{ fontSize: 16, color: '#7b1fa2' }} />
              <Typography variant="caption" fontWeight={700} color="#7b1fa2">
                AI Suggestions
              </Typography>
            </Box>
            {MOCK_AI_SUGGESTIONS.filter(s => s.type === 'block').map((s, i) => (
              <Box key={i} sx={{ mb: 0.5, p: 0.5, bgcolor: '#fff', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TargetIcon target={s.target || 'domain'} />
                  <Typography variant="caption" sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.7rem' }}>{s.pattern}</Typography>
                  <Chip label={`${Math.round(s.confidence * 100)}%`} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                  <Button size="small" sx={{ minWidth: 0, fontSize: '0.65rem', px: 0.5 }}>Add</Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block', mt: 0.2, pl: 2.5 }}>
                  {s.reason}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        /* DevTools-style network panel */
        <NetworkDevToolsPanel onAddBlockRule={(suggestion) => {
          setPrefillRule(suggestion);
          setShowLive(false);
          setShowAdd(true);
        }} />
      )}
    </Box>
  );
};

// ─── Mockup 2: Extension Sidebar — Elements Tab ─────────────

const ExtensionElementsTab = () => {
  const [rules, setRules] = useState(MOCK_ELEMENT_RULES);
  const [picking, setPicking] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <Box sx={{ px: 1.5, py: 1, display: 'flex', gap: 1, borderBottom: '1px solid #e0e0e0' }}>
        <Button
          startIcon={<PickerIcon />}
          variant={picking ? 'contained' : 'outlined'}
          color={picking ? 'error' : 'primary'}
          size="small"
          onClick={() => setPicking(!picking)}
          sx={{ flex: 1 }}
        >
          {picking ? 'Picking...' : 'Pick Element'}
        </Button>
        <Tooltip title="AI suggest elements to hide">
          <Button startIcon={<AiIcon />} variant="outlined" size="small" color="secondary">
            Suggest
          </Button>
        </Tooltip>
      </Box>

      {picking && (
        <Alert severity="info" sx={{ mx: 1.5, mt: 1, py: 0 }} icon={<PickerIcon sx={{ fontSize: 16 }} />}>
          <Typography variant="caption">Click any element on the page to hide it</Typography>
        </Alert>
      )}

      {/* Element rules list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Typography variant="caption" fontWeight={700} sx={{ px: 1.5, py: 0.5, display: 'block', bgcolor: '#f5f5f5' }}>
          Hidden Elements ({rules.filter(r => r.isActive).length} active)
        </Typography>
        <List dense disablePadding>
          {rules.map(rule => (
            <ListItem key={rule.id} sx={{ py: 0.5, px: 1.5, borderBottom: '1px solid #f5f5f5' }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {rule.isActive ? <HideIcon sx={{ fontSize: 16, color: '#f44336' }} /> : <VisibilityIcon sx={{ fontSize: 16, color: '#4caf50' }} />}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                    {rule.selector}
                  </Typography>
                }
                secondary={
                  <Box sx={{ mt: 0.3 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {rule.description}
                    </Typography>
                    {rule.suggestedBlock && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
                        <BlockIcon sx={{ fontSize: 12, color: '#c62828' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#c62828' }}>
                          Also block {rule.suggestedBlock}?
                        </Typography>
                        <Button size="small" sx={{ minWidth: 0, fontSize: '0.6rem', px: 0.3, py: 0 }}>Yes</Button>
                      </Box>
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                  <Switch size="small" checked={rule.isActive} />
                  <IconButton size="small"><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                </Box>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        {/* AI Suggestions */}
        <Box sx={{ p: 1.5, bgcolor: '#f3e5f5', borderTop: '2px solid #ce93d8' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <AiIcon sx={{ fontSize: 16, color: '#7b1fa2' }} />
            <Typography variant="caption" fontWeight={700} color="#7b1fa2">
              AI Suggestions — Elements to Hide
            </Typography>
          </Box>
          {MOCK_AI_SUGGESTIONS.filter(s => s.type === 'hide').map((s, i) => (
            <Box key={i} sx={{ mb: 0.5, p: 0.5, bgcolor: '#fff', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <HideIcon sx={{ fontSize: 14, color: '#f44336' }} />
                <Typography variant="caption" sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.7rem' }}>{s.selector}</Typography>
                <Chip label={`${Math.round(s.confidence * 100)}%`} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.2 }}>
                {s.reason}
              </Typography>
              <Button size="small" sx={{ mt: 0.3, fontSize: '0.65rem' }}>Hide This</Button>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Save bar */}
      <Box sx={{ p: 1, borderTop: '1px solid #e0e0e0', bgcolor: '#fff3e0', display: 'flex', gap: 1 }}>
        <Button variant="contained" size="small" fullWidth>Save Rules</Button>
        <Button variant="outlined" size="small">Discard</Button>
      </Box>
    </Box>
  );
};

// ─── Mockup 3: Extension Sidebar Full ────────────────────────

const ExtensionSidebarMockup = () => {
  const [tab, setTab] = useState(0);

  return (
    <Paper sx={{ width: 380, height: 700, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '2px solid #1976d2' }} elevation={4}>
      {/* Header */}
      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
        <IconButton size="small"><BackIcon fontSize="small" /></IconButton>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>Khan Academy Login</Typography>
        <IconButton size="small"><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
        sx={{ borderBottom: '1px solid #e0e0e0', minHeight: 36,
          '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: '0.8rem', fontWeight: 600 } }}>
        <Tab icon={<PlayIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Steps" />
        <Tab icon={<NetworkIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Filters" />
        <Tab icon={<ElementsIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Elements" />
      </Tabs>

      {/* Tab content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {tab === 0 && (
          <Box sx={{ p: 2, color: 'text.secondary' }}>
            <Typography variant="body2">Step editor goes here (same UnifiedStepEditor component)</Typography>
          </Box>
        )}
        {tab === 1 && <ExtensionFiltersTab />}
        {tab === 2 && <ExtensionElementsTab />}
      </Box>
    </Paper>
  );
};

// ─── Mockup 4: App List with Filter Indicators ──────────────

const AppListMockup = () => (
  <Paper sx={{ overflow: 'hidden' }} elevation={2}>
    <Box sx={{ bgcolor: '#f5f5f5', px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography variant="h6" fontWeight={700}>Scripts</Typography>
      <Chip label="Filter status indicators on each app group" size="small" color="info" />
    </Box>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700 }}>App</TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="center">Scripts</TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="center">Content Filters</TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {MOCK_APPS_WITH_FILTERS.map(app => (
          <TableRow key={app.appKey} hover>
            <TableCell>
              <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                {app.name}
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Chip label={app.scripts} size="small" />
            </TableCell>
            <TableCell align="center">
              <FilterStatusBadge status={app.status} networkRules={app.networkRules} elementRules={app.elementRules} />
            </TableCell>
            <TableCell align="right">
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                <Tooltip title="Edit scripts"><IconButton size="small"><EditIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Manage filters">
                  <IconButton size="small" color="primary"><ShieldIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

// ─── Main Mockup Page ────────────────────────────────────────

const ContentFilteringMockup = () => {
  return (
    <Box>
      {/* Section 1: Extension Sidebar */}
      <Typography variant="h5" fontWeight={700} gutterBottom>
        1. Extension Sidebar — Filters & Elements Tabs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When a script is selected, the sidebar shows three tabs: Steps (existing), Filters (network rules),
        and Elements (CSS element hiding). Both tabs support AI-powered suggestions via Claude.
      </Typography>
      <Box sx={{ display: 'flex', gap: 3, mb: 6, flexWrap: 'wrap' }}>
        <ExtensionSidebarMockup />
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>Filters Tab Features</Typography>
            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
              <li>View global + per-app network rules</li>
              <li>Toggle rules on/off instantly</li>
              <li>Add new block/allow rules (domain, URL pattern, regex)</li>
              <li>Live network requests view — see what's blocked in real-time</li>
              <li>AI suggestions: Claude analyzes page and suggests rules</li>
              <li>"Reload Filters" pushes changes to the content blocker</li>
            </ul>
          </Alert>
          <Alert severity="info">
            <Typography variant="body2" fontWeight={600}>Elements Tab Features</Typography>
            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
              <li>Pick any page element to hide (reuses selector picker)</li>
              <li>CSS selector-based hiding, persisted per app</li>
              <li>Cross-reference: hiding a social button suggests blocking its domain</li>
              <li>AI suggestions: Claude identifies social/chat/external elements to hide</li>
              <li>Hide rules auto-injected on page load via chrome.scripting.insertCSS</li>
            </ul>
          </Alert>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 2: App List Indicators */}
      <Typography variant="h5" fontWeight={700} gutterBottom>
        2. App List — Filter Status Indicators
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The Scripts page shows filter status per app: shield icon with rule count, or "No filters" warning
        for apps that haven't been configured yet. Shield icon links to the filter management page.
      </Typography>
      <Box sx={{ mb: 6 }}>
        <AppListMockup />
      </Box>
    </Box>
  );
};

export default ContentFilteringMockup;
